import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import { colors, radius, shadows } from '../../styles/theme';
import { formatTRY, formatSayi } from '../../utils/analytics/format';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const IkramIptalPage = () => {
  useAuth();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const today = new Date();

  const [baslangic, setBaslangic] = useUrlState(
    'ikram_iptal_baslangic',
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
  );

  const [bitis, setBitis] = useUrlState(
    'ikram_iptal_bitis',
    today.toISOString().split('T')[0]
  );

  const [lokasyonId, setLokasyonId] = useUrlState(
    'ikram_iptal_lokasyon',
    ''
  );

  const [tur, setTur] = useUrlState(
    'ikram_iptal_tur',
    ''
  );

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let url =
        `${API_URL}/api/raporlar/ikram-iptal` +
        `?tarih_baslangic=${baslangic}` +
        `&tarih_bitis=${bitis}`;

      if (lokasyonId) {
        url += `&lokasyon_id=${lokasyonId}`;
      }

      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        const result = await res.json();
        setData(result);
      } else {
        const e = await res.json().catch(() => ({}));
        setError(
          e.detail || `İkram - İptal kayıtları yüklenemedi (${res.status})`
        );
      }
    } catch (err) {
      logError('İkram - İptal kayıtları yüklenemedi:', err);
      setError('Sunucu hatası — bağlantıyı kontrol edin');
    } finally {
      setLoading(false);
    }
  }, [baslangic, bitis, lokasyonId]);

  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, {
      headers: getAuthHeaders(),
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setLokasyonlar(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  const detay = (data?.detay || []).filter((d) => {
    if (!tur) return true;
    return d.tur === tur;
  });

  const toplamMaliyet = detay.reduce(
    (sum, d) => sum + (Number(d.maliyet_toplam) || 0),
    0
  );

  return (
    <div>
      <div
        style={{
          background: colors.surface,
          borderRadius: radius.lg,
          padding: '14px 16px',
          boxShadow: shadows.md,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
              Başlangıç
            </div>
            <input
              type="date"
              value={baslangic}
              onChange={(e) => setBaslangic(e.target.value)}
              style={{
                padding: '7px 9px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: 12,
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
              Bitiş
            </div>
            <input
              type="date"
              value={bitis}
              onChange={(e) => setBitis(e.target.value)}
              style={{
                padding: '7px 9px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: 12,
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
              Lokasyon
            </div>
            <select
              value={lokasyonId}
              onChange={(e) => setLokasyonId(e.target.value)}
              style={{
                padding: '7px 9px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: 12,
                background: colors.surface,
                minWidth: 180,
              }}
            >
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map((lokasyon) => (
                <option key={lokasyon.id} value={lokasyon.id}>
                  {lokasyon.ad}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
              Tür
            </div>
            <select
              value={tur}
              onChange={(e) => setTur(e.target.value)}
              style={{
                padding: '7px 9px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: 12,
                background: colors.surface,
                minWidth: 140,
              }}
            >
              <option value="">Tümü</option>
              <option value="IKRAM">İkram</option>
              <option value="IPTAL">İptal</option>
            </select>
          </div>
        </div>
      </div>

      <div
        style={{
          background: colors.surface,
          borderRadius: radius.lg,
          boxShadow: shadows.md,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#374151',
              }}
            >
              İkram - İptal Kayıtları
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                color: '#9ca3af',
              }}
            >
              {loading ? 'Yükleniyor...' : `${detay.length} kayıt`}
            </div>
          </div>

          {!loading && !error && (
            <div
              style={{
                fontSize: 13,
                color: '#6b7280',
              }}
            >
              Toplam Maliyet:{' '}
              <strong style={{ color: '#374151' }}>
                {formatTRY(toplamMaliyet)}
              </strong>
            </div>
          )}
        </div>

        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: 50,
              color: '#9ca3af',
            }}
          >
            Yükleniyor...
          </div>
        ) : error ? (
          <div
            style={{
              textAlign: 'center',
              padding: 50,
              color: '#dc2626',
            }}
          >
            {error}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: '#f8fafc',
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  {[
                    'Tarih',
                    'Lokasyon',
                    'Ürün',
                    'Tür',
                    'Adet',
                    'Birim Fiyat',
                    'Toplam Tutar',
                    'Birim Maliyet',
                    'Toplam Maliyet',
                  ].map((baslik) => (
                    <th
                      key={baslik}
                      style={{
                        padding: '10px 9px',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: '#4b5563',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {baslik}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {detay.map((d) => (
                  <tr
                    key={d.id}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <td style={{ padding: '9px', whiteSpace: 'nowrap' }}>
                      {d.tarih || '-'}
                    </td>

                    <td style={{ padding: '9px', whiteSpace: 'nowrap' }}>
                      {d.lokasyon_ad || '-'}
                    </td>

                    <td style={{ padding: '9px', fontWeight: 500, minWidth: 180 }}>
                      {d.urun_ad || '-'}
                    </td>

                    <td style={{ padding: '9px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          borderRadius: 12,
                          whiteSpace: 'nowrap',
                          fontSize: 11,
                          fontWeight: 600,
                          background: '#f3f4f6',
                          color: '#4b5563',
                          border: '1px solid #d1d5db',
                        }}
                      >
                        {d.tur === 'IKRAM' ? 'İkram' : d.tur === 'IPTAL' ? 'İptal' : d.tur || '-'}
                      </span>
                    </td>

                    <td
                      style={{
                        padding: '9px',
                        textAlign: 'right',
                        fontWeight: 600,
                      }}
                    >
                      {formatSayi(d.miktar)}
                    </td>

                    <td
                      style={{
                        padding: '9px',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatTRY(d.birim_fiyat || 0, { decimals: 2 })}
                    </td>

                    <td
                      style={{
                        padding: '9px',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatTRY(d.toplam_tutar || 0, { decimals: 2 })}
                    </td>

                    <td
                      style={{
                        padding: '9px',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {d.birim_maliyet == null
                        ? '—'
                        : formatTRY(d.birim_maliyet, { decimals: 2 })}
                    </td>

                    <td
                      style={{
                        padding: '9px',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                      }}
                    >
                      {d.maliyet_toplam == null
                        ? '—'
                        : formatTRY(d.maliyet_toplam, { decimals: 2 })}
                    </td>
                  </tr>
                ))}

                {detay.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      style={{
                        padding: 30,
                        textAlign: 'center',
                        color: '#9ca3af',
                      }}
                    >
                      Seçilen tarih aralığında İkram - İptal kaydı bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IkramIptalPage;
